import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { FEATURE_FLAGS_CONFIG, FeatureFlagUsage } from '@/components/FeatureFlagPlayground/utils';
import Iconify from '@/components/Iconify';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import { expandFeatureFlags, FeatureFlagName, ServerSideFlags } from '@/components/session/session.store';
import { useSession } from '@/components/session/useSession';
import { useSandboxFeatureFlags } from '@/hooks/useSandboxFeatureFlags';
import { isPrediumEmployee } from '@/utils/Roles';
// @ts-ignore
import easterEggQR from './easter-egg.png';

export default function FeatureFlagPlayground() {
  const [open, setOpen] = useState(false);
  const [localFlags, setLocalFlags] = useState<ServerSideFlags | null>(null);
  const [defaultValues, setDefaultValues] = useState<ServerSideFlags | null>(null);
  const [filter, setFilter] = useState<FeatureFlagUsage | 'all' | 'coffee'>('all');
  const [searchText, setSearchText] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSession();

  // Always show in local development, only show for @predium.de users in sandbox
  const isLocalDevelopment = import.meta.env.MODE === 'development';
  const isSandbox = import.meta.env.VITE_SANDBOX === 'true';
  const isPrediumUser = isPrediumEmployee(user.email);

  const isEnabled = isLocalDevelopment || (isSandbox && isPrediumUser);

  const { loading, featureFlagDefaultValues, currentFeatureFlags, updateFeatureFlag, resetFeatureFlags } =
    useSandboxFeatureFlags({ isSandboxOrDev: isEnabled });

  // Fetch default values from backend when component mounts
  useEffect(() => {
    if (isEnabled && !defaultValues && featureFlagDefaultValues) {
      setDefaultValues(expandFeatureFlags(featureFlagDefaultValues));
    }
  }, [isEnabled, defaultValues, featureFlagDefaultValues]);

  // Initialize local flags with current backend state
  useEffect(() => {
    if (currentFeatureFlags) {
      setLocalFlags(expandFeatureFlags(currentFeatureFlags));
    }
  }, [currentFeatureFlags]);

  // Reset filter if coffee tab is selected but not available
  useEffect(() => {
    if (filter === 'coffee' && !isLocalDevelopment) {
      setFilter('all');
    }
  }, [filter, isLocalDevelopment]);

  const handleFlagChange = async (flagName: FeatureFlagName, value: boolean) => {
    if (!localFlags || loading) return;

    // Optimistically update local state
    const updatedFlags = {
      ...localFlags,
      [flagName]: value,
    };
    setLocalFlags(updatedFlags);

    try {
      await updateFeatureFlag({
        flag: flagName,
        value: value,
      });

      enqueueSnackbar('Feature flag updated successfully', {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    } catch (error) {
      console.error('Failed to update feature flag:', error);

      enqueueSnackbar('Failed to update feature flag', {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });

      // Revert local state on error
      const revertedFlags = {
        ...localFlags,
        [flagName]: !value,
      };
      setLocalFlags(revertedFlags);
    }
  };

  const resetFlags = async () => {
    if (!defaultValues || loading) return;

    try {
      // Optimistically reset local state to backend defaults
      setLocalFlags({ ...defaultValues });

      await resetFeatureFlags();

      enqueueSnackbar('Feature flags reset to defaults', {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    } catch (error) {
      console.error('Failed to reset feature flags:', error);

      enqueueSnackbar('Failed to reset feature flags', {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    }
  };

  if (!isEnabled || !localFlags) {
    return null;
  }

  const allFlagEntries = (Object.entries(localFlags) as [FeatureFlagName, boolean][]).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  // Apply usage filter
  const usageFilteredEntries =
    filter === 'coffee'
      ? [] // No flags to show in coffee tab
      : filter === 'all'
        ? allFlagEntries
        : allFlagEntries.filter(([flagName]) => FEATURE_FLAGS_CONFIG[flagName].usage === filter);

  // Apply search filter
  const searchLower = searchText.trim().toLowerCase();
  const flagEntries = searchLower
    ? usageFilteredEntries.filter(([flagName]) => {
        const flagNameLower = flagName.toLowerCase();
        const descriptionLower = FEATURE_FLAGS_CONFIG[flagName].description.toLowerCase();
        return flagNameLower.includes(searchLower) || descriptionLower.includes(searchLower);
      })
    : usageFilteredEntries;

  const enabledCount = allFlagEntries.filter(([, value]) => value).length;
  const totalCount = allFlagEntries.length;

  return (
    <>
      {/* Subtle drawer handle at top middle */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          cursor: 'pointer',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          px: 2,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: 1,
          transition: 'all 0.2s ease-in-out',
          opacity: 0.8,
          '&:hover': {
            opacity: 1,
            transform: 'translateX(-50%) translateY(2px)',
            boxShadow: 2,
          },
          '.MuiTypography-caption': {
            color: 'primary.contrastText',
          },
        }}
      >
        <Iconify icon={ICONS.EVA_SETTINGS_2_OUTLINE} width={16} height={16} />
        <PTypography variant="caption">Feature flags</PTypography>
        <Chip
          label={`${enabledCount}/${totalCount}`}
          size="small"
          sx={{
            height: 16,
            fontSize: '0.65rem',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'inherit',
            '& .MuiChip-label': {
              px: 0.5,
            },
          }}
        />
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: 600,
            maxHeight: 600,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Iconify icon={ICONS.EVA_SETTINGS_2_FILL} color="primary" />
              <PTypography variant={'h6'}>Feature Flag Playground</PTypography>
              <Chip
                label={`${enabledCount}/${totalCount} enabled`}
                size="small"
                color={enabledCount > 0 ? 'primary' : 'default'}
              />
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small">
              <Iconify icon={ICONS.EVA_CLOSE_FILL} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box mb={2}>
            <PTypography variant={'body2'} color="secondary">
              Toggle feature flags for this {import.meta.env.VITE_SANDBOX === 'true' ? 'sandbox' : 'development'}{' '}
              instance. Changes are persisted across sessions and synchronized with the backend.
            </PTypography>
          </Box>

          {/* Filter tabs */}
          <Box mb={2} sx={{ flexShrink: 0 }}>
            <Tabs
              value={filter}
              onChange={(_, newValue) => setFilter(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40 }}
            >
              <Tab label="All" value="all" sx={{ minHeight: 40, py: 1 }} />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>Client</span>
                    <Chip
                      label={allFlagEntries.filter(([name]) => FEATURE_FLAGS_CONFIG[name].usage === 'client').length}
                      size="small"
                      sx={{ height: 16, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                value="client"
                sx={{ minHeight: 40, py: 1 }}
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>Backend</span>
                    <Chip
                      label={allFlagEntries.filter(([name]) => FEATURE_FLAGS_CONFIG[name].usage === 'backend').length}
                      size="small"
                      sx={{ height: 16, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                value="backend"
                sx={{ minHeight: 40, py: 1 }}
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>Both</span>
                    <Chip
                      label={allFlagEntries.filter(([name]) => FEATURE_FLAGS_CONFIG[name].usage === 'both').length}
                      size="small"
                      sx={{ height: 16, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                value="both"
                sx={{ minHeight: 40, py: 1 }}
              />
              {isLocalDevelopment ? (
                <Tab
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Iconify icon={ICONS.EVA_COFFEE_FILL} width={16} height={16} />
                      <span>Buy me a coffee</span>
                    </Box>
                  }
                  value="coffee"
                  sx={{ minHeight: 40, py: 1 }}
                />
              ) : null}
            </Tabs>
          </Box>

          <Box mb={2} sx={{ flexShrink: 0 }}>
            <TextField
              fullWidth
              autoFocus
              placeholder="Search flags by name or description..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Box>

          {filter === 'coffee' && isLocalDevelopment ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 3,
              }}
            >
              <PTypography variant={'h5'}>☕ Buy me a coffee!</PTypography>
              <PTypography variant={'body1'} color="secondary" sx={{ maxWidth: 400 }}>
                If you enjoy using this feature flag playground, consider supporting the developer!
              </PTypography>
              <Box
                sx={{
                  width: 160,
                  height: 160,
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'background.paper',
                  backgroundImage: `url("${easterEggQR}")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                }}
              />

              <Button
                variant="soft"
                onClick={() => {
                  window.open('https://www.youtube.com/watch?v=xvFZjo5PgG0&list=RDxvFZjo5PgG0', '_blank');
                }}
              >
                Or click here to buy me a coffee
              </Button>
            </Box>
          ) : (
            <List
              sx={{
                flex: 1,
                overflowY: 'auto',
                '& .MuiListItem-root': {
                  px: 0,
                },
              }}
            >
              {flagEntries.map(([flagName, isEnabled], index) => (
                <React.Fragment key={flagName}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Box width="100%">
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <PTypography variant={'body2'}>{flagName}</PTypography>
                            <Chip
                              label={FEATURE_FLAGS_CONFIG[flagName].usage}
                              size="small"
                              variant="soft"
                              color={
                                FEATURE_FLAGS_CONFIG[flagName].usage === 'client'
                                  ? 'info'
                                  : FEATURE_FLAGS_CONFIG[flagName].usage === 'backend'
                                    ? 'error'
                                    : 'success'
                              }
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                textTransform: 'capitalize',
                              }}
                            />
                          </Box>
                          <PTypography variant={'body2'} color="secondary">
                            {FEATURE_FLAGS_CONFIG[flagName].description}
                          </PTypography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isEnabled}
                              onChange={(e) => handleFlagChange(flagName, e.target.checked)}
                              color="primary"
                              disabled={loading}
                            />
                          }
                          label=""
                          sx={{ mr: 0 }}
                        />
                      </Box>
                    </Box>
                  </ListItem>
                  {index < flagEntries.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetFlags} color="inherit" disabled={loading}>
            Reset to Defaults
          </Button>
          <Button onClick={() => setOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
